import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Section, CTAButton } from './UI';
import { CheckCircle2, XCircle, Quote } from 'lucide-react';
export function AudienceAndProofSection() {
  return <div className="bg-black text-white" data-id="element-9">
      {/* For / Not For */}
      <Section className="border-t border-white/10 bg-white/[0.02]" data-id="element-10">
        <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto" data-id="element-11">
          <div data-id="element-12">
            <h3 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3" data-id="element-13">
              Este programa <span className="text-green-400" data-id="element-14">ES para ti</span>{' '}
              si:
            </h3>
            <ul className="space-y-6" data-id="element-15">
              {['Tienes conocimientos o habilidades que otros te piden que les enseñes.', 'Estás cansado de consumir contenido y quieres empezar a crear.', 'Quieres una fuente de ingresos extra que no dependa de tu tiempo directo.', 'Estás dispuesto a seguir un sistema paso a paso y tomar acción.'].map((item, i) => <li key={i} className="flex gap-4 items-start" data-id="element-16">
                  <CheckCircle2 className="w-6 h-6 text-green-400 shrink-0 mt-1" data-id="element-17" />
                  <span className="text-gray-300 text-lg" data-id="element-18">{item}</span>
                </li>)}
            </ul>
          </div>

          <div data-id="element-19">
            <h3 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3" data-id="element-20">
              Este programa <span className="text-red-400" data-id="element-21">NO es para ti</span>{' '}
              si:
            </h3>
            <ul className="space-y-6" data-id="element-22">
              {['Buscas hacerte rico de la noche a la mañana sin esfuerzo.', 'Quieres que la IA haga el 100% del trabajo sin aportar tu toque humano.', 'Prefieres seguir coleccionando cursos en lugar de aplicar lo que aprendes.', 'No estás dispuesto a invertir al menos unas horas a la semana en tu futuro.'].map((item, i) => <li key={i} className="flex gap-4 items-start" data-id="element-23">
                  <XCircle className="w-6 h-6 text-red-400 shrink-0 mt-1" data-id="element-24" />
                  <span className="text-gray-400 text-lg" data-id="element-25">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="mt-16 text-center" data-id="element-26">
          <CTAButton text="ESTO ES PARA MÍ. QUIERO EMPEZAR." variant="secondary" data-id="element-27" />
        </div>
      </Section>

      {/* Social Proof / Results */}
      <Section className="py-24" data-id="element-28">
        <div className="text-center mb-20" data-id="element-29">
          <h2 className="text-4xl md:text-6xl font-extrabold mb-6" data-id="element-30">
            Resultados Reales
          </h2>
          <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto" data-id="element-31">
            Personas normales construyendo activos extraordinarios.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-32">
          {[{
          name: 'Carlos Mendoza',
          role: 'Consultor de Negocios',
          text: 'Llevaba meses queriendo escribir un ebook. Con este sistema y los prompts de IA, lo terminé en un fin de semana. Ya he generado mis primeras ventas en automático.',
          img: 'https://i.pravatar.cc/150?img=11',
          result: '+ $450 USD en su primera semana',
          sales: '15 ventas'
        }, {
          name: 'Laura Gómez',
          role: 'Diseñadora Gráfica',
          text: 'El módulo de empaquetado y oferta es oro puro. Pasé de vender mis servicios por hora a tener una plantilla digital que se vende sola mientras duermo.',
          img: 'https://i.pravatar.cc/150?img=5',
          result: 'Ingresos pasivos recurrentes',
          sales: '32 ventas'
        }, {
          name: 'Miguel Ángel Torres',
          role: 'Emprendedor Digital',
          text: 'No sabía nada de IA. Este libro me dio el paso a paso exacto. Dejé de sentirme abrumado y por fin lancé algo al mercado. La mejor inversión del año.',
          img: 'https://i.pravatar.cc/150?img=8',
          result: 'Lanzó su primer curso en 5 días',
          sales: '28 ventas'
        }].map((testimonial, i) => <motion.div key={i} whileInView={{
          opacity: 1,
          y: 0
        }} initial={{
          opacity: 0,
          y: 30
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.15
        }} className="bg-[#0a0a0a] border border-white/10 rounded-3xl p-8 relative shadow-2xl flex flex-col" data-id="element-33">
              <Quote className="absolute top-8 right-8 w-10 h-10 text-white/5" data-id="element-34" />

              {/* Fake Sales Notification */}
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3 mb-6 flex items-center gap-3" data-id="element-35">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-black font-bold text-xs" data-id="element-36">
                  $
                </div>
                <div data-id="element-37">
                  <div className="text-green-400 font-bold text-sm" data-id="element-38">
                    Nuevo pago recibido
                  </div>
                  <div className="text-gray-400 text-xs" data-id="element-39">
                    {testimonial.sales} generadas
                  </div>
                </div>
              </div>

              <p className="text-gray-300 mb-8 relative z-10 text-lg leading-relaxed flex-1" data-id="element-40">
                "{testimonial.text}"
              </p>

              <div className="bg-white/5 rounded-2xl p-4 mb-6" data-id="element-41">
                <div className="text-yellow-400 font-bold text-sm uppercase tracking-wider mb-1" data-id="element-42">
                  Resultado:
                </div>
                <div className="text-white font-medium" data-id="element-43">
                  {testimonial.result}
                </div>
              </div>

              <div className="flex items-center gap-4 mt-auto" data-id="element-44">
                <img src={testimonial.img} alt={testimonial.name} loading="lazy" decoding="async" width={56} height={56} className="w-14 h-14 rounded-full border-2 border-white/10" data-id="element-45" />
                <div data-id="element-46">
                  <h4 className="font-bold text-white text-lg" data-id="element-47">
                    {testimonial.name}
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-48">{testimonial.role}</p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </Section>
    </div>;
}